import { sortBy } from "lodash";
import React, { SVGAttributes } from "react";

import { Cartesian } from "@examspark/types";

export const getSlope = (
  coord1: Cartesian.Coordinate,
  coord2: Cartesian.Coordinate,
) => {
  return (coord2.y - coord1.y) / (coord2.x - coord1.x);
};

export const getYInterceptAtX = (
  coord1: Cartesian.Coordinate,
  coord2: Cartesian.Coordinate,
  x: number,
) => {
  const slope = getSlope(coord1, coord2);
  const yInt = coord1.y - slope * coord1.x;
  const yIntAtX = x * slope + yInt;
  return yIntAtX;
};

export const areCoordinatesEqual = (
  coord1: Cartesian.Coordinate,
  coord2: Cartesian.Coordinate,
) => {
  //   console.log(coord1, coord2);
  if (coord1.x === coord2.x && coord1.y === coord2.y) {
    return true;
  }

  return false;
};

export const getStartEndForLine = (
  graph: CartesianGraphProps,
  points: Cartesian.Coordinate[],
): {
  start: Cartesian.Coordinate;
  end: Cartesian.Coordinate;
} => {
  const slope = getSlope(points[0], points[1]);
  if (slope === Infinity || slope === -Infinity) {
    return {
      start: getImageCoordsFromCartesianCoords(graph, {
        x: points[0].x,
        y: graph.y.min,
      }),
      end: getImageCoordsFromCartesianCoords(graph, {
        x: points[1].x,
        y: graph.y.max,
      }),
    };
  }

  return {
    start: getImageCoordsFromCartesianCoords(graph, {
      x: graph.x.min,
      y: getYInterceptAtX(points[0], points[1], graph.x.min),
    }),
    end: getImageCoordsFromCartesianCoords(graph, {
      x: graph.x.max,
      y: getYInterceptAtX(points[0], points[1], graph.x.max),
    }),
  };
};

export const getImageCoordsFromCartesianCoords = (
  graph: CartesianGraphProps,
  coord: Cartesian.Coordinate,
  offset?: Cartesian.ImageCoordinate,
) => {
  const cartesianWidth = graph.x.max - graph.x.min;
  const cartesianHeight = graph.y.max - graph.y.min;

  let graphWidth = graph.width;
  if (graph.padding?.left) graphWidth -= graph.padding.left;
  if (graph.padding?.right) graphWidth -= graph.padding.right;

  let graphHeight = graph.height;
  if (graph.padding?.top) graphHeight -= graph.padding.top;
  if (graph.padding?.bottom) graphHeight -= graph.padding.bottom;

  const xScale = graphWidth / cartesianWidth;
  const yScale = -graphHeight / cartesianHeight;

  return {
    x:
      xScale * (coord.x - graph.x.min) +
      (offset?.x || 0) +
      (graph.padding?.left || 0),
    y:
      yScale * (coord.y - graph.y.max) +
      (offset?.y || 0) +
      (graph.padding?.top || 0),
  };
};

export const getCartesianCoordsFromImageCoords = (
  graph: CartesianGraphProps,
  coord: Cartesian.ImageCoordinate,
) => {
  const cartesianWidth = graph.x.max - graph.x.min;
  const cartesianHeight = graph.y.max - graph.y.min;

  let graphWidth = graph.width;
  if (graph.padding?.left) graphWidth -= graph.padding.left;
  if (graph.padding?.right) graphWidth -= graph.padding.right;

  let graphHeight = graph.height;
  if (graph.padding?.top) graphHeight -= graph.padding.top;
  if (graph.padding?.bottom) graphHeight -= graph.padding.bottom;

  const xScale = graphWidth / cartesianWidth;
  const yScale = graphHeight / cartesianHeight;

  return {
    x: (coord.x - (graph.padding?.left || 0)) / xScale + graph.x.min,
    y: (-coord.y + (graph.padding?.top || 0)) / yScale + graph.y.max,
  };
};

export const getLocalCoordinate = (
  svg: SVGElement,
  globalCoord: Cartesian.Coordinate,
): Cartesian.Coordinate => {
  const dim = svg.getBoundingClientRect();
  const x = globalCoord.x - (dim?.left || 0);
  const y = globalCoord.y - (dim?.top || 0);

  return {
    x,
    y,
  };
};

export const translateCoordinates = (
  coords: Cartesian.Coordinate[],
  delta: Cartesian.Coordinate,
) => {
  return coords.map((c) => ({
    x: c.x + delta.x,
    y: c.y + delta.y,
  }));
};

export const getRoundedCartesianCoordinateLegacy = (
  graph: CartesianGraphProps,
  coord: Cartesian.ImageCoordinate,
) => {
  const cart = getCartesianCoordsFromImageCoords(graph, coord);
  return { x: Math.round(cart.x), y: Math.round(cart.y) };
};

export const getRoundedCartesianCoordiate = (
  graph: CartesianGraphProps,
  coord: Cartesian.ImageCoordinate,
): Cartesian.Coordinate => {
  const cart = getCartesianCoordsFromImageCoords(graph, coord);
  const rounded = {
    x: getClosestTick(graph.x, cart.x),
    y: getClosestTick(graph.y, cart.y),
  };

  // console.log("coord", rounded);
  return rounded;
};

export const getClosestTick = (axis: Cartesian.Axis, position: number) => {
  const ticks = getTicks(axis).map((t) => t.value);
  // console.log("ticks", ticks);
  position = Number(position.toFixed(2));

  let lower = ticks[0];
  let upper = ticks[1];

  for (let index = 0; index < ticks.length; index++) {
    if (position >= ticks[index]) {
      lower = ticks[index];
      upper = ticks[index + 1] ?? ticks[index];
    }
  }

  const distanceToLower = Math.abs(position - lower);
  const distanceToUpper = Math.abs(position - upper);

  if (distanceToLower < distanceToUpper) {
    return lower;
  } else {
    return upper;
  }
};

export const applyOffsetToCoordinate = (
  coord: Cartesian.Coordinate,
  offset: Cartesian.Coordinate,
) => {
  return {
    x: coord.x + offset.x,
    y: coord.y + offset.y,
  };
};

export const getDistance = (
  coord1: Cartesian.ImageCoordinate,
  coord2: Cartesian.ImageCoordinate,
) => {
  return Math.sqrt(
    Math.pow(coord2.x - coord1.x, 2) + Math.pow(coord2.y - coord1.y, 2),
  );
};

export const getAxisIntegers = (axis: Cartesian.Axis) => {
  const integers: number[] = [];

  for (let index = Math.ceil(axis.min); index <= Math.ceil(axis.max); index++) {
    if (axis.gridDistance !== undefined && index % axis.gridDistance !== 0) {
      continue;
    }

    integers.push(index);
  }

  return integers;
};

export const getTickIntegers = (axis: Cartesian.Axis) => {
  const integers: number[] = [];

  for (let index = Math.ceil(axis.min); index < Math.ceil(axis.max); index++) {
    if (axis.tickDistance !== undefined && index % axis.tickDistance !== 0) {
      continue;
    }

    integers.push(index);
  }

  return integers;
};

export const getTicks = (axis: Cartesian.Axis) => {
  const ticks: {
    type: "major" | "minor";
    value: number;
  }[] = [];

  let i = 0;
  while (i < axis.max) {
    ticks.push({
      type: "major",
      value: Number(i.toFixed(2)),
    });

    if (axis.minorTicks) {
      const minorTickDistance =
        (axis.tickDistance || 1) / (axis.minorTicks + 1);

      let idx = i;
      for (let index = 0; index < axis.minorTicks; index++) {
        idx += minorTickDistance;
        ticks.push({
          type: "minor",
          value: Number(idx.toFixed(2)),
        });
      }
    }

    i += axis.gridDistance || axis.tickDistance || 1;
  }

  i = 0;
  while (i > axis.min) {
    if (i !== 0) {
      ticks.push({
        type: "major",
        value: Number(i.toFixed(2)),
      });

      if (axis.minorTicks) {
        const minorTickDistance =
          (axis.tickDistance || 1) / (axis.minorTicks + 1);

        let idx = i;
        for (let index = 0; index < axis.minorTicks; index++) {
          idx += minorTickDistance;
          ticks.push({
            type: "minor",
            value: Number(idx.toFixed(2)),
          });
        }
      }
    }

    i -= axis.gridDistance || axis.tickDistance || 1;
  }

  return sortBy(ticks, ["value"]);
};
export interface CartesianGraphProps extends Cartesian.Graph {
  mode?: string;
  onChange?: (shapes: Cartesian.Shape[]) => void;
}
export const CartesianGraphContext = React.createContext<
  | {
      canvas: SVGSVGElement | null;
      graph: CartesianGraphProps;
      mode?: string;
      snapFn?: (
        graph: CartesianGraphProps,
        coord: Cartesian.ImageCoordinate,
      ) => Cartesian.Coordinate;
    }
  | undefined
>(undefined);
export interface LineProps
  extends Omit<
    SVGAttributes<SVGLineElement>,
    "end" | "onDrag" | "onDragStart" | "onDragEnd"
  > {
  start: Cartesian.Coordinate;
  end: Cartesian.Coordinate;
}
